import { makeStyles, TableCell, Tooltip } from '@material-ui/core';
import { LevelOfSkillDto } from 'utils/wemble-api.generated';

/** Above this many levels a named column per level stops being readable on
 *  desktop too (Born runs ten), so the header collapses to a scale. */
export const MAX_NAMED_LEVEL_COLUMNS = 5;

const useStyles = makeStyles({
  cell: {
    textAlign: 'center',
  },
  // Named columns wrap to two lines before truncating: long level names
  // ("Har lyst til å lære") fit a narrow column without losing meaning.
  label: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: 1.25,
    padding: '0 4px',
  },
  // One header across every level column: lowest level on the left, highest on
  // the right, a rule between. Individual level names live in the tooltip on
  // each bar.
  scale: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '0 2px',
    minWidth: 0,
  },
  // Each end wraps to at most two lines rather than ellipsizing: at phone
  // width a one-line end shrinks to "WOULD…", which names nothing.
  scale__end: {
    maxWidth: '42%',
    lineHeight: 1.25,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  scale__end_last: {
    textAlign: 'right',
  },
  scale__rule: {
    flex: 1,
    minWidth: 8,
    height: 1,
    backgroundColor: 'currentColor',
    opacity: 0.35,
    position: 'relative',
    // Arrowhead so the direction of the scale reads without words.
    '&::after': {
      content: '""',
      position: 'absolute',
      right: -1,
      top: -3,
      borderLeft: '5px solid currentColor',
      borderTop: '3.5px solid transparent',
      borderBottom: '3.5px solid transparent',
    },
  },
});

/** Header cells for the level columns of a level grid (the competence People
 *  tab and the person card's Grid). `compact` collapses them into one scale;
 *  otherwise each column is named, ellipsized, with the full name on hover. */
const LevelScaleHeader = ({
  levels,
  compact,
  onClick,
  color,
}: {
  levels: LevelOfSkillDto[];
  compact: boolean;
  onClick?: () => void;
  color?: string;
}) => {
  const classes = useStyles();
  const style = { color, cursor: onClick ? 'pointer' : undefined };

  if (levels.length === 0) return null;

  if (compact) {
    const first = levels[0];
    const last = levels[levels.length - 1];
    return (
      <TableCell colSpan={levels.length} onClick={onClick} style={style}>
        <div className={classes.scale}>
          <span className={classes.scale__end}>{first.name}</span>
          <span className={classes.scale__rule} />
          <span className={`${classes.scale__end} ${classes.scale__end_last}`}>{last.name}</span>
        </div>
      </TableCell>
    );
  }

  return (
    <>
      {levels.map((level) => (
        <TableCell key={level._id} className={classes.cell} onClick={onClick} style={style}>
          <Tooltip title={level.name ?? ''} arrow placement="top">
            <span className={classes.label}>{level.name}</span>
          </Tooltip>
        </TableCell>
      ))}
    </>
  );
};

export default LevelScaleHeader;
